import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsUpDown, Search, Download, Columns3 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Generic DataTable.
 * columns: [{ key, label, render?, sortable? }]
 * data: array
 * server-side pagination via total/page/pageSize + callbacks
 */
export default function DataTable({
  columns,
  data,
  total,
  page,
  pageSize,
  loading,
  searchValue,
  onSearchChange,
  onPageChange,
  onSortChange,
  sortBy,
  sortDir,
  filters,
  rightActions,
  emptyText = "No records found.",
  testId,
  onRowClick,
  exportFilename = "export.csv",
}) {
  const [visibleCols, setVisibleCols] = useState(() => columns.map((c) => c.key));
  const visibleColumns = useMemo(
    () => columns.filter((c) => visibleCols.includes(c.key)),
    [columns, visibleCols]
  );

  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 25)));

  function toggleCol(key) {
    setVisibleCols((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }

  function handleSort(col) {
    if (!col.sortable || !onSortChange) return;
    if (sortBy === col.key) onSortChange(col.key, sortDir === 1 ? -1 : 1);
    else onSortChange(col.key, 1);
  }

  function exportCsv() {
    const cols = visibleColumns;
    const header = cols.map((c) => `"${c.label}"`).join(",");
    const rows = (data || []).map((row) =>
      cols
        .map((c) => {
          const v = row[c.key];
          const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
          return `"${s.replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = exportFilename;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div data-testid={testId || "data-table"} className="hah-card overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E7EB] flex-wrap">
        <div className="flex items-center gap-2 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg px-3 py-1.5 min-w-[240px]">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input
            data-testid="table-search-input"
            value={searchValue || ""}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            placeholder="Search…"
            className="bg-transparent outline-none text-sm w-full text-[#1F2937] placeholder-[#94A3B8]"
          />
        </div>
        {filters}
        <div className="flex-1" />
        {rightActions}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="column-visibility-button" className="border-[#E5E7EB] text-[#1F2937]">
              <Columns3 className="w-4 h-4 mr-1.5" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {columns.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                checked={visibleCols.includes(c.key)}
                onCheckedChange={() => toggleCol(c.key)}
                data-testid={`column-toggle-${c.key}`}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="outline"
          size="sm"
          onClick={exportCsv}
          data-testid="export-csv-button"
          className="border-[#E5E7EB] text-[#1F2937]"
        >
          <Download className="w-4 h-4 mr-1.5" />
          Export
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F7FA] sticky top-0 z-10">
            <tr>
              {visibleColumns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => handleSort(c)}
                  className={`text-left text-[11px] tracking-wider uppercase font-semibold text-[#6B7280] px-4 py-3 border-b border-[#E5E7EB] ${
                    c.sortable ? "cursor-pointer hover:text-[#0B7E8C]" : ""
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable && <ChevronsUpDown className="w-3 h-3 opacity-60" />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-4 py-10 text-center text-[#6B7280]">
                  Loading…
                </td>
              </tr>
            ) : (data || []).length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-4 py-10 text-center text-[#6B7280]">
                  {emptyText}
                </td>
              </tr>
            ) : (
              (data || []).map((row, idx) => (
                <tr
                  key={row.id || idx}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  data-testid={`row-${row.id || idx}`}
                  className={`border-b border-[#E5E7EB] last:border-b-0 hover:bg-[#EAF8FA]/40 ${
                    onRowClick ? "cursor-pointer" : ""
                  }`}
                >
                  {visibleColumns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-[#1F2937] align-middle">
                      {c.render ? c.render(row) : row[c.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E7EB] text-sm text-[#6B7280]">
        <div data-testid="pagination-info">
          Showing <span className="font-medium text-[#1F2937]">{(data || []).length}</span> of{" "}
          <span className="font-medium text-[#1F2937]">{total || 0}</span> records
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="pagination-prev"
            onClick={() => onPageChange && onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="w-8 h-8 rounded-md border border-[#E5E7EB] flex items-center justify-center disabled:opacity-40 hover:bg-[#F5F7FA]"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-2">
            Page <span className="font-medium text-[#1F2937]">{page}</span> / {totalPages}
          </span>
          <button
            data-testid="pagination-next"
            onClick={() => onPageChange && onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="w-8 h-8 rounded-md border border-[#E5E7EB] flex items-center justify-center disabled:opacity-40 hover:bg-[#F5F7FA]"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
